import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TenantSettingService } from '@services/api/tenant-setting.service';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { WebhookViewModel } from '../tenant-setting-apikey/tenant-setting-apikey.component';

/** バックアップメールアドレスのViewModel */
export type BackupEmailStrViewModel = {
  /** バックアップメールアドレスのViewModel */
  backupEmailStr: string;
};

/** フォームグループ */
type BackupEmailFormGroup = FormGroup<{ backupEmail: FormControl<string> }>;

@Component({
  selector: 'app-tenant-setting-backup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './tenant-setting-backup.component.html',
  styleUrls: ['./tenant-setting-backup.component.scss'],
})
export class TenantSettingBackupComponent {
  @Input() public heading: string = 'バックアップメール送信先メールアドレス';

  @Input() public tooltipText: string =
    '各月1日に、自動で下記のメールアドレスに、前月分のデバイスのセンサーデータのバックアップファイル(zip)のダウンロードURLを記載したメールをお送りいたします。URLの有効期間は発行から7日間です。';

  /** バックアップメールアドレスのカンマ区切りの文字列 */
  @Input() public backupEmailStr: string = '';

  @Input() public webhook: WebhookViewModel = {
    apiKeyName: '',
    webhookUrl: '',
    keyHeaderName: '',
    apiKeyValue: '',
    hasApikey: false,
  };
  /** 更新を通知 */
  @Output() updated = new EventEmitter<string>();

  /** バックアップメールアドレス */
  public backupEmailList: string[] = [];

  /** フォームグループ */
  public formGroup: BackupEmailFormGroup;

  /** 編集フラグ */
  public updating: boolean = false;

  constructor(
    private tenantSettingService: TenantSettingService,
    private dialog: MatDialog,
  ) {
    this.formGroup = new FormGroup({
      backupEmail: new FormControl<string>(
        this.backupEmailStr.split(',').join('\n'),
        {
          nonNullable: true,
        },
      ),
    });
  }

  /** 編集モードの切り替え */
  public toggleUpdating(): void {
    this.updating = !this.updating;
    if (this.updating) {
      this.formGroup.controls['backupEmail'].setValue(this.backupEmailStr);
    } else {
      this.formGroup.reset();
    }
  }

  /** 編集モードをキャンセル */
  public cancelEditing(): void {
    this.updating = false;
    this.formGroup.reset();
  }

  /** バックアップメールアドレスを更新する */
  public updateBackupEmail(): void {
    // 改行やカンマの位置を整理する。
    const formattedEmail = this.formatBackupEmail();
    const invalidEmails = this.validateEmails(formattedEmail);
    // 無効な入力が存在する場合エラーダイアログを表示
    if (invalidEmails.length > 0) {
      this.showErrorDialog();
      return;
    }

    const backupEmailRequest = formattedEmail.join(',');
    this.updated.emit(backupEmailRequest);
    this.updating = false;
  }

  /** 入力されたbackupEmailから空行と空文字を排除し、カンマ区切りの文字列に整理する
   * @returns フォーマットされたメールアドレスののリスト
   */
  private formatBackupEmail(): string[] {
    const newBackupEmail = this.formGroup.controls['backupEmail'].value;
    const formattedEmail = newBackupEmail.replace(/\r?\n|\r/g, ',');
    this.backupEmailList = formattedEmail
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0); // 空行を除去

    return this.backupEmailList;
  }

  /**
   * 無効なメールアドレスの形式のものをリストとして返す。
   * @param emailList メールアドレスのリスト
   * @returns 不正な形式のメールアドレスのリスト
   * */
  private validateEmails(emailList: string[]): string[] {
    // メールアドレスの正規表現
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailList.filter((email) => !emailPattern.test(email));
  }

  /** 入力が無効な形式のときのエラーダイアログを表示する。 */
  private showErrorDialog(): void {
    const alertDialogOptions: AlertDialogOption = {
      type: 'error',
      title: 'エラー',
      message: ` 改行またはカンマ区切りのメールアドレスの形式で入力してください。`,
    };
    this.dialog.open(AlertDialogComponent, {
      width: '600px',
      data: alertDialogOptions,
    });
  }
}
